import { type CheckboxChangeEvent } from "antd/lib/checkbox";
import { Component } from "react";

import { cn } from "@btnet/utilities-core";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { OpenedFrom } from "types/enum";

import { routes } from "utilities/routes";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCheckbox } from "commonComponents/btWrappers/BTCheckbox/BTCheckbox";
import {
    BTConfirm,
    BTConfirmContent,
    BTConfirmFooter,
} from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTForm, BTFormItem } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTFormattedPlural } from "commonComponents/btWrappers/BTFormattedPlural/BTFormattedPlural";
import { BTLink, BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";

import { type BillDocumentType } from "entity/bill/Bill/Bill.api.types";
import { RouteBill } from "entity/bill/Bill/RouteBill";
import {
    POPaymentStatus,
    PurchaseOrderPaymentStatusTag,
} from "entity/purchaseOrderPayment/common/PurchaseOrderPaymentStatus/PurchaseOrderPaymentStatus";

import "./PaymentVoidConfirm.less";

export type PaymentVoidConfirmFormActions = "void" | "cancel" | "voidAll" | undefined;

export interface ICanVoidPayment {
    voidTitle: string;
    voidMessage: string;
    voidCheckboxMessage?: string;
    voidTooltip?: string;
    additionalVoidAlert?: string;
    billsTiedToSamePayment?: BillForVoiding[];
    billsBeingVoided?: BillForVoiding[];
}

export class BillForVoiding {
    constructor(data: any) {
        this.jobId = data.jobId;
        this.billId = data.billId;
        this.purchaseOrderId = data.purchaseOrderId;
        this.billNumber = data.billNumber;
        this.status = data.status;
        this.checkNumber = data.checkNumber;
        this.documentType = (data as { documentType?: BillDocumentType }).documentType;
    }
    jobId: number;
    billId: number;
    purchaseOrderId: number | null;
    billNumber: string;
    status: POPaymentStatus;
    checkNumber: string | null;
    documentType?: BillDocumentType;
}

export interface IPaymentVoidConfirmProps {
    payment: ICanVoidPayment;
    visible: boolean;
    onOk: (isDeleteFromAccountingChecked?: boolean, billIds?: number[]) => void;
    onCancel: () => void;
    /**
     * Set to true when this workflow is it via a checked action from a list view. In that case, don't handle routing inside in this component and allow the list to handle it
     */
    isMassVoid?: boolean;
    actionBeingPerformed?: PaymentVoidConfirmFormActions;
    modalConfig?: IModalConfiguration;
    multipleBillsSelected?: boolean;
}

interface IPaymentVoidConfirmState {
    isDeleteFromAccountingChecked: boolean;
}

export default class PaymentVoidConfirm extends Component<
    IPaymentVoidConfirmProps,
    IPaymentVoidConfirmState
> {
    state: Readonly<IPaymentVoidConfirmState> = {
        isDeleteFromAccountingChecked: false,
    };

    static contextType = BuilderInfoContext;
    declare context: React.ContextType<typeof BuilderInfoContext>;

    private handleConfirm = async (voidRelatedBills: boolean) => {
        const { onOk, payment, isMassVoid } = this.props;

        let billIdsToVoid: number[] | undefined = undefined;

        if (voidRelatedBills && !isMassVoid) {
            billIdsToVoid = [
                ...(payment.billsBeingVoided?.map((x) => x.billId) ?? []),
                ...(payment.billsTiedToSamePayment
                    ?.filter((x) => x.status !== POPaymentStatus.Void)
                    .map((x) => x.billId) ?? []),
            ];
        } else if (voidRelatedBills && isMassVoid) {
            // with mass void, only send back the other bills, as the billsBeingVoided will not include all the billIds
            billIdsToVoid = [
                ...payment
                    .billsTiedToSamePayment!.filter((x) => x.status !== POPaymentStatus.Void)
                    .map((x) => x.billId),
            ];
        }

        onOk(
            payment.voidCheckboxMessage ? this.state.isDeleteFromAccountingChecked : undefined,
            billIdsToVoid,
        );
    };

    private handleDeleteFromAccountingChange = (e: CheckboxChangeEvent) => {
        this.setState({
            isDeleteFromAccountingChecked: e.target.checked,
        });
    };

    render() {
        const {
            payment,
            visible,
            isMassVoid,
            onCancel,
            actionBeingPerformed,
            modalConfig,
            multipleBillsSelected,
        } = this.props;

        const numberOfRelatedBillsVoidable =
            payment.billsTiedToSamePayment?.filter((x) => x.status !== POPaymentStatus.Void)
                ?.length ?? 0;
        const numberOfSelectedBillsVoidable =
            payment.billsBeingVoided?.filter((x) => x.status !== POPaymentStatus.Void)?.length ?? 0;

        let voidBillButtonText;

        if (numberOfRelatedBillsVoidable > 0 && numberOfRelatedBillsVoidable === 1) {
            voidBillButtonText = "Void this bill only";
        } else if (numberOfRelatedBillsVoidable > 0 && numberOfSelectedBillsVoidable > 1) {
            voidBillButtonText = `Void these ${numberOfSelectedBillsVoidable} bills only`;
        } else if (payment.billsTiedToSamePayment && payment.billsTiedToSamePayment.length > 0) {
            voidBillButtonText = "Void this bill";
        } else {
            voidBillButtonText = `Void bill${multipleBillsSelected ? "s" : ""}`;
        }

        return (
            <BTConfirm
                title={payment.voidTitle}
                visible={visible}
                className="PaymentVoidConfirm"
                fitToContent
            >
                {!isMassVoid && (
                    <RouteBill modalConfig={modalConfig} openedFrom={OpenedFrom.PurchaseOrder} />
                )}
                <BTConfirmContent className="ConfirmContent">
                    <div className="margin-bottom-sm">{payment.voidMessage}</div>
                    {payment.billsTiedToSamePayment && payment.billsBeingVoided && (
                        <BTForm>
                            <BTFormItem
                                label={
                                    <BTFormattedPlural
                                        value={payment.billsBeingVoided.length}
                                        one="This bill"
                                        other="These bills"
                                    />
                                }
                            >
                                {payment.billsBeingVoided.map((x) => {
                                    const useFullPageBill =
                                        this.context?.flags.billsFullPageConversion;
                                    const Link = useFullPageBill ? BTLink : BTLinkRelative;
                                    return (
                                        <div key={x.billId}>
                                            <Link
                                                isUnderline={false}
                                                key={x.billId}
                                                to={routes.bill.getDetailsLink(x.billId, x.jobId)}
                                                title={x.billNumber}
                                            >
                                                Bill {x.billNumber}{" "}
                                            </Link>
                                            {x.checkNumber && <span>(check #{x.checkNumber})</span>}
                                            {x.status === POPaymentStatus.Void && (
                                                <PurchaseOrderPaymentStatusTag
                                                    status={x.status}
                                                    documentType={x.documentType}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </BTFormItem>
                            <BTFormItem label="Other bills">
                                {payment.billsTiedToSamePayment.map((x) => {
                                    const useFullPageBill =
                                        this.context?.flags.billsFullPageConversion;
                                    const Link = useFullPageBill ? BTLink : BTLinkRelative;
                                    return (
                                        <div key={x.billId}>
                                            <Link
                                                isUnderline={false}
                                                key={x.billId}
                                                to={routes.bill.getDetailsLink(x.billId, x.jobId)}
                                                title={x.billNumber}
                                            >
                                                Bill {x.billNumber}{" "}
                                            </Link>
                                            {x.checkNumber && <span>(check #{x.checkNumber})</span>}
                                            {x.status === POPaymentStatus.Void && (
                                                <PurchaseOrderPaymentStatusTag
                                                    status={x.status}
                                                    documentType={x.documentType}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </BTFormItem>
                        </BTForm>
                    )}
                    {payment.voidCheckboxMessage && (
                        <>
                            <BTCheckbox
                                id="deleteFromAccounting"
                                data-testid="deleteFromAccounting"
                                onChange={this.handleDeleteFromAccountingChange}
                                checked={this.state.isDeleteFromAccountingChecked}
                                className="margin-bottom-sm"
                            >
                                {payment.voidCheckboxMessage}
                            </BTCheckbox>
                            {payment.voidTooltip && (
                                <BTPopover
                                    removeSpacing
                                    autoAdjustOverflow={false}
                                    overlayStyle={{ position: "fixed" }}
                                    content={
                                        <div
                                            // eslint-disable-next-line react/no-danger
                                            dangerouslySetInnerHTML={{
                                                __html: payment.voidTooltip,
                                            }}
                                        />
                                    }
                                />
                            )}
                        </>
                    )}
                    {payment.additionalVoidAlert && (
                        <BTAlert
                            type="warning"
                            message={payment.additionalVoidAlert}
                            data-testid="additionalVoidAlert"
                        />
                    )}
                </BTConfirmContent>
                <BTConfirmFooter className="padding-bottom-md">
                    <BTButtonDeprecated<PaymentVoidConfirmFormActions>
                        id="cancel"
                        data-testid="cancel"
                        onClick={onCancel}
                        className="margin-right-xs"
                        loadingAction="cancel"
                        actionBeingPerformed={actionBeingPerformed}
                    >
                        Cancel
                    </BTButtonDeprecated>
                    <BTButtonDeprecated<PaymentVoidConfirmFormActions>
                        id="void"
                        data-testid="void"
                        type={numberOfRelatedBillsVoidable ? "secondary" : "destruction"}
                        className={cn({ "margin-right-xs": numberOfRelatedBillsVoidable })}
                        onClick={() => this.handleConfirm(false)}
                        loadingAction="void"
                        actionBeingPerformed={actionBeingPerformed}
                    >
                        {voidBillButtonText}
                    </BTButtonDeprecated>
                    {numberOfRelatedBillsVoidable > 0 && (
                        <BTButtonDeprecated
                            data-testid="voidAll"
                            id="voidAll"
                            type="destruction"
                            onClick={() => this.handleConfirm(true)}
                            loadingAction="voidAll"
                            actionBeingPerformed={actionBeingPerformed}
                        >
                            Void all {numberOfRelatedBillsVoidable + numberOfSelectedBillsVoidable}{" "}
                            bills
                        </BTButtonDeprecated>
                    )}
                </BTConfirmFooter>
            </BTConfirm>
        );
    }
}
